import {styled, CSSObject} from "@mui/material/styles"
import {Typography} from "@mui/material"
import merge from "lodash/merge"
import {breakpoints} from "@next/themes"
import {ImageStyles, TextStyles} from "./models"
import {removeUnsetProps} from "../utils"

interface ContainerStyles {
    width?: string
    gap?: string
    isSearchBannerCarousel?: boolean
}
interface StyledImageProps extends ImageStyles {
    gap?: string
}

interface ImageChipWidthAndMargin {
    width?: string
    minWidth?: string
    maxWidth?: string
    marginLeft?: string
    marginRight?: string
}

const LARGE_CHIP_WIDTH = 80

/**
 * Calculates carousel item margins based on whether it's in carousel mode
 * and if a gap is provided
 *
 * @param isCarousel Whether the component is in carousel mode
 * @param gap Optional gap value for calculated margins
 * @param defaultMargin Optional default margin to use when no gap (defaults to "0.25rem")
 * @returns Object with marginLeft and marginRight properties
 */
export const getCarouselItemMargins = (
    isCarousel: boolean,
    gap?: string,
    defaultMargin = "0.25rem",
): {marginLeft: string; marginRight: string} => {
    if (!isCarousel) {
        return {marginLeft: "0", marginRight: "0"}
    }

    if (gap) {
        return {
            marginLeft: `calc(${gap}/2)`,
            marginRight: `calc(${gap}/2)`,
        }
    }

    return {marginLeft: defaultMargin, marginRight: defaultMargin}
}

export const ChipsContainer = styled("div", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$containerStyles: ContainerStyles; $chipCount: number; $isSearchBannerCarousel?: boolean}>(
    ({$containerStyles, $isSearchBannerCarousel}) => {
        const override = removeUnsetProps({
            gap: $containerStyles.gap,
        }) as CSSObject
        let gapFromWidth = "0.5rem"
        if ($containerStyles.width) {
            if (Number($containerStyles.width) > LARGE_CHIP_WIDTH) {
                gapFromWidth = "0.5rem"
            } else {
                gapFromWidth = "0"
            }
        }
        return merge(
            {
                display: `${$isSearchBannerCarousel ? "block" : "flex"}`,
                paddingBottom: "1rem",
                lineHeight: 1.43,
                letterSpacing: "0.0075rem",
                textAlign: "center",
                overflowX: "auto",
                gap: gapFromWidth,
                "&::-webkit-scrollbar": {
                    display: "none",
                },
                "div:first-of-type": {
                    marginInlineStart: "auto",
                },
                "div:last-of-type": {
                    marginInlineEnd: "auto",
                },
            },
            override,
        )
    },
)

export const ImageChip = styled("div", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$containerStyles?: ContainerStyles; $isSearchBannerCarousel?: boolean}>(
    ({$containerStyles, $isSearchBannerCarousel}) => {
        // if the chips have a gap set, we need to use minWidth rather than width to stop them overlapping
        // when there are many chips
        let style: ImageChipWidthAndMargin = {}
        if ($containerStyles?.gap) {
            style = {
                minWidth: "5rem",
                ...getCarouselItemMargins($isSearchBannerCarousel || false, $containerStyles?.gap),
            }
        } else if ($containerStyles?.width) {
            // if the width is greater than the large chip width, we want to set the width to auto
            if (Number($containerStyles.width) > LARGE_CHIP_WIDTH) {
                style = {
                    maxWidth: `${$containerStyles.width}px`,
                    ...getCarouselItemMargins($isSearchBannerCarousel || false),
                }
            } else {
                style = {width: `${((Number($containerStyles.width) / 4) * 5).toString()}px`}
            }
        } else {
            style = {width: "5rem"}
        }
        return style as CSSObject
    },
)

export const StyledLink = styled("a")({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textDecoration: "none",
})

export const StyledTypography = styled(Typography, {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$textStyles: TextStyles}>(({theme, $textStyles}) => {
    const override = removeUnsetProps({
        color: $textStyles.textColor,
        fontWeight: $textStyles.fontWeight,
        textTransform: $textStyles.textTransform,
        fontSize: $textStyles.fontSize,
        WebkitLineClamp: $textStyles.lineClamp,
    } as CSSObject)
    return merge(
        {
            fontFamily: theme.colours.plp.searchBanner.searchListItemLabel.fontFamily,
            color: theme.colours.plp.searchBanner.default.banner.imageChips.textColor,
            fontWeight: theme.colours.plp.searchBanner.searchListItemLabel.fontWeight,
            textTransform: theme.colours.plp.searchBanner.searchListItemLabel.textTransform,
            fontSize: theme.colours.plp.searchBanner.searchListItemLabel.fontSize,
            textDecoration: theme.colours.plp.searchBanner.searchListItemLabel.textDecoration,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            WebkitLineClamp: 3,
            textOverflow: "ellipsis",
            whiteSpace: "pre-line",
        } as CSSObject,
        override,
    )
})

// add gap to ImageStyles as if we have gap we don't want to also add a margin
export const StyledImage = styled("img", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$imageStyles: StyledImageProps}>(({theme, $imageStyles}) => {
    const override = removeUnsetProps({
        borderColor: $imageStyles.borderColor,
        borderRadius: $imageStyles.borderRadius,
        height: $imageStyles.height ? `${$imageStyles.height}px` : undefined,
        width: $imageStyles.width ? `${$imageStyles.width}px` : undefined,
    } as CSSObject)
    // if the image chips are large, or we have a user sepcified gap, we don't want to add a margin
    const margin =
        $imageStyles.gap || Number($imageStyles?.width) > LARGE_CHIP_WIDTH
            ? {}
            : {marginLeft: "0.375rem", marginRight: "0.375rem"}
    return merge(
        {
            backgroundColor: theme.colours.plp.searchBanner.default.banner.imageChips.backgroundColor,
            border: "1px solid #9e9e9e",
            borderColor: theme.colours.plp.searchBanner.default.banner.imageChips.enabled.borderColor,
            borderRadius: theme.colours.plp.searchBanner.imageChip.default.borderRadius,
            height: "4rem",
            width: "4rem",
            ...margin,
            marginBottom: "0.25rem",
        },
        override,
    )
})

export const EmblaCarousel = styled("div")(() => ({
    maxWidth: "calc(100% - 3rem)",
    margin: "auto",
    position: "relative",
    "--slide-height": "19rem",
    "--slide-spacing": "1rem",
    "--slide-size": "100%",
    "--slide-spacing-sm": "1.6rem",
    "--slide-size-sm": "50%",
    "--slide-spacing-lg": "2rem",
    "--slide-size-lg": "calc(100% / 3)",

    [`@media (max-width: ${breakpoints.values.md - 1}px)`]: {
        maxWidth: "100%",
    },
}))

export const EmblaCarouselContainer = styled("div")(() => ({
    backfaceVisibility: "hidden",
    display: "flex",
    touchAction: "pan-y pinch-zoom",
    marginLeft: "calc(var(--slide-spacing) * -1)",
}))

export const EmblaButton = styled("button", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$imageStyles: StyledImageProps}>(({theme, $imageStyles}) => {
    return {
        backgroundColor: theme.colours.palette.primary.contrastText,
        cursor: "pointer",
        border: 0,
        width: "1.75rem",
        height: "2.75rem",
        zIndex: 1,
        position: "absolute",
        "--search-banner-image-chip-height": $imageStyles.height ? `${$imageStyles.height}px` : "4rem",
        top: `calc(var(--search-banner-image-chip-height) / 2 - 1.375rem)`,

        "&:disabled": {
            display: "none",
        },

        "&.embla_button_next": {
            right: "-1.75rem",
        },
        "&.embla_button_previous": {
            left: "-1.76rem",
        },
        [`@media (max-width: ${breakpoints.values.md - 1}px)`]: {
            display: "none",
        },
    }
})

export const EmblaButtonArrow = styled("img")(() => ({
    width: "0.4rem",
    objectFit: "contain",

    "&.embla_button_arrow_previous": {
        transform: "rotate(180deg)",
    },
}))
